/**
 * Grouping + filtering for the board activity feed.
 *
 * The raw feed is a flat, newest-first list where the same person often produces
 * a burst of events in a row — rendering it one-row-per-event repeats the avatar
 * and name for every line and shows the same relative timestamp over and over.
 * These helpers fold that list into day groups, and within a day into per-actor
 * sessions, so the feed reads as a chronology instead of a wall of repetition.
 *
 * Pure functions — no React, no `Date.now()` baked in (callers pass `now`) so the
 * labels are deterministic under test.
 */

import type { BoardActivityItem, BoardPersonMini } from '@hygge/shared';

/** A run of consecutive events by one actor, inside one day. */
export interface ActivitySession {
  /** Stable list key. */
  key: string;
  actor: BoardPersonMini | null;
  /** Board updates render as a card and never merge with task events. */
  kind: 'update' | 'task';
  items: BoardActivityItem[];
  /** Timestamp of the newest item in the session — what the header shows. */
  at: string;
}

export interface ActivityDay {
  /** Local calendar day, `YYYY-MM-DD`. */
  key: string;
  label: string;
  sessions: ActivitySession[];
}

/**
 * A quiet hour between two events by the same person is a different sitting, not
 * one burst — splitting keeps the session's single timestamp honest.
 */
export const SESSION_GAP_MS = 60 * 60 * 1000;

/** Coarse buckets for the "type" filter — the raw `field` values are too granular. */
export type ActivityCategory = 'update' | 'created' | 'status' | 'assignee' | 'content' | 'other';

export const ACTIVITY_CATEGORY_LABEL: Record<ActivityCategory, string> = {
  update: 'Updates',
  created: 'Created',
  status: 'Status changes',
  assignee: 'Assignments',
  content: 'Title & description',
  other: 'Other changes',
};

export function activityCategory(item: BoardActivityItem): ActivityCategory {
  if (item.kind === 'update') return 'update';
  if (item.type === 'created') return 'created';
  if (item.field === 'status') return 'status';
  if (item.field === 'assignee') return 'assignee';
  if (item.field === 'title' || item.field === 'description') return 'content';
  return 'other';
}

function actorOf(item: BoardActivityItem): BoardPersonMini | null {
  return item.kind === 'update' ? item.author : item.actor;
}

/** Local calendar day key. Not `toISOString()` — that would bucket by UTC and
 *  push late-evening events into tomorrow for anyone east of Greenwich. */
function dayKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function dayLabel(iso: string, now: Date): string {
  const d = new Date(iso);
  const today = dayKey(now);
  const yesterday = dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  const key = dayKey(d);
  if (key === today) return 'Today';
  if (key === yesterday) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    // Only spell out the year when it isn't the current one.
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** Short clock time for a session header (the exact instant lives in a tooltip). */
export function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export interface ActivityFilters {
  search?: string;
  /** Employee ids; empty/undefined means everyone. */
  actorIds?: string[];
  /** Categories; empty/undefined means every type. */
  categories?: ActivityCategory[];
}

export function filterActivity(
  items: BoardActivityItem[],
  filters: ActivityFilters = {},
): BoardActivityItem[] {
  const term = filters.search?.trim().toLowerCase() ?? '';
  const actorIds = filters.actorIds?.length ? new Set(filters.actorIds) : null;
  const categories = filters.categories?.length ? new Set(filters.categories) : null;

  return items.filter((item) => {
    if (categories && !categories.has(activityCategory(item))) return false;

    if (actorIds) {
      const actor = actorOf(item);
      if (!actor || !actorIds.has(actor.id)) return false;
    }

    if (term) {
      const actor = actorOf(item);
      const haystack =
        item.kind === 'update'
          ? [item.body, actor?.firstName, actor?.lastName]
          : [item.taskKey, item.taskTitle, item.field, item.fromValue, item.toValue, actor?.firstName, actor?.lastName];
      if (!haystack.some((v) => v?.toLowerCase().includes(term))) return false;
    }

    return true;
  });
}

/**
 * Folds a newest-first feed into day groups of per-actor sessions. Input order is
 * preserved — the server already sorts, so this never re-sorts and can't disagree
 * with it.
 */
export function groupActivity(items: BoardActivityItem[], now: Date): ActivityDay[] {
  const days: ActivityDay[] = [];

  for (const item of items) {
    const at = new Date(item.createdAt);
    const key = dayKey(at);
    const actor = actorOf(item);
    const kind = item.kind === 'update' ? 'update' : 'task';

    let day = days[days.length - 1];
    if (!day || day.key !== key) {
      day = { key, label: dayLabel(item.createdAt, now), sessions: [] };
      days.push(day);
    }

    const last = day.sessions[day.sessions.length - 1];
    const sameActor = last && (last.actor?.id ?? null) === (actor?.id ?? null);
    const withinGap =
      last && Math.abs(new Date(last.items[last.items.length - 1].createdAt).getTime() - at.getTime()) <= SESSION_GAP_MS;
    // Updates carry a body and render as their own card, so they never absorb —
    // and are never absorbed by — neighbouring one-line task events.
    const mergeable = last && last.kind === 'task' && kind === 'task' && sameActor && withinGap;

    if (mergeable) {
      last.items.push(item);
    } else {
      day.sessions.push({ key: `${item.kind}-${item.id}`, actor, kind, items: [item], at: item.createdAt });
    }
  }

  return days;
}
